import React, { useEffect, useState } from "react"
import { Box, Link } from "@mui/material"
import { BigNumber, ContractReceipt } from "ethers"
import { useTranslation } from "react-i18next"
import {
  POOLS_MAP,
  REWARD_TOKENS,
  FER,
  PoolName,
  Token,
  LCRO_WCRO_POOL_NAME,
} from "src/constants"
import { useActiveWeb3React } from "src/hooks"
import { useNavigate } from "react-router-dom"
import { useIsMobile } from "src/hooks/useIsMobile"
import { commify } from "src/utils"
import parseStringToBigNumber, {
  formatNumber,
} from "src/utils/parseStringToBigNumber"
import { getAddress } from "src/utils/addressHelpers"
import { calculatePrice } from "src/utils"
import AmountInput from "./AmountInput"
import Button from "./Button"
import CustomCard from "./CustomCard"
import FerroPriceText from "./FerroPriceText"
import Modal from "./Modal"
import Text from "./Text"
import { enqueueToast } from "./Toastify"
import TransactionSummary from "./TransactionSummary"
import Web3Status from "./Web3Status"
import { formatUnits } from "ethers/lib/utils"
import { useBoolean } from "src/hooks/useBoolean"
import HarvestOptionModal from "./HarvestOptionModal"
import DepositModal from "src/pages/Stake/components/DepositModal"
import useXFerState from "src/pages/Stake/hooks/useXFerState"
import { HarvestMode } from "src/state/xFer/types"
import PieIcon from "src/assets/icons/pie.svg"
import InfoSection from "./InfoSection"
import { CroPoolWarning } from "./CroWarning"
import UnstakeLpModal from "./BoostFarming/UnstakeLpModal"

interface Props {
  lpWalletBalance: BigNumber
  approveAndStake: (amount: BigNumber) => Promise<ContractReceipt | void>
  unstake: (amount: BigNumber) => Promise<ContractReceipt | void>
  amountStaked: BigNumber
  boostedRewards: BigNumber
  rewardBalance: BigNumber
  rewardTokens: {
    tokens: string[]
    earnings: BigNumber[]
  }
  harvest: () => Promise<void>
  harvestBoost: () => Promise<void>
  token: Token
  isBoosted: boolean
  poolName: PoolName
}

export default function PoolStakePage({
  lpWalletBalance,
  approveAndStake,
  unstake,
  amountStaked,
  boostedRewards,
  rewardBalance,
  rewardTokens,
  harvest,
  harvestBoost,
  token,
  isBoosted,
  poolName,
}: Props) {
  const { t } = useTranslation()
  const { account } = useActiveWeb3React()
  const navigate = useNavigate()
  const [stakeValue, setStakeValue] = useState("")
  const [unstakeValue, setUnstakeValue] = useState("")
  const [stakeError, setStakeError] = useState<string | null>(null)
  const [unstakeError, setUnstakeError] = useState<string | null>(null)
  const [showConfirmModal, setShowConfirmModal] = useState<
    "stake" | "unstake" | false
  >(false)
  const [showModal, openModal, closeModal] = useBoolean(false)
  const [showLpModal, openLpModal, closeLpModal] = useBoolean(false)
  const [isBoost, setIsBoost] = useState<boolean>(false)
  const isMobile = useIsMobile()
  const { openedDepositModal } = useXFerState()
  const pool = POOLS_MAP[poolName]
  let enableHarvest = rewardBalance.gt(0)

  const validateInput = (value: string, balance: BigNumber) => {
    if (parseStringToBigNumber(value, 18).value.gt(balance)) {
      return "Insufficient Balance"
    } else {
      return null
    }
  }

  const summary =
    showConfirmModal === "stake"
      ? [
          {
            label: t("Stake"),
            amount: commify(stakeValue),
            token: token,
          },
        ]
      : [
          {
            label: t("Unstake"),
            amount: commify(unstakeValue),
            token: token,
          },
        ]

  const onConfirm = async () => {
    try {
      if (showConfirmModal === "stake") {
        await approveAndStake(parseStringToBigNumber(stakeValue, 18).value)
      } else {
        await unstake(parseStringToBigNumber(unstakeValue, 18).value)
      }
    } catch {
      enqueueToast(
        "error",
        showConfirmModal === "stake" ? t("Stake failed") : t("Unstake failed"),
      )
    } finally {
      setShowConfirmModal(false)
      setStakeValue("")
      setUnstakeValue("")
    }
  }

  useEffect(() => {
    setStakeError(validateInput(stakeValue, lpWalletBalance))
  }, [lpWalletBalance, stakeValue])

  useEffect(() => {
    setUnstakeError(validateInput(unstakeValue, amountStaked))
  }, [amountStaked, unstakeValue])

  const renderEarningsV1 = () => {
    return (
      <Box>
        <Text bold my={0.5}>{`${FER.symbol} ${formatNumber(
          formatUnits(rewardBalance, FER.decimals),
          2,
          6,
        )}`}</Text>
        <FerroPriceText amount={rewardBalance} />
      </Box>
    )
  }

  const renderEarningsV2 = () => {
    enableHarvest = false
    return (
      <Box>
        {rewardTokens?.tokens &&
          rewardTokens.tokens.map((address, index) => {
            const token = REWARD_TOKENS.find(
              (t) => getAddress(t.addresses) === address,
            )
            const earning = rewardTokens.earnings[index]
            if (token) {
              if (earning.gt("0")) {
                enableHarvest = true
              }
              //TODO: Fetch token price dynamically
              return (
                (earning.gt(0) || index < rewardTokens.earnings.length - 1) && (
                  <>
                    <Text bold my={0.5}>{`${token.symbol} ${formatNumber(
                      formatUnits(earning, token.decimals),
                      2,
                      6,
                    )}`}</Text>
                    {token === FER ? (
                      <FerroPriceText amount={earning} />
                    ) : (
                      <Text
                        size="micro"
                        color="#ADADAD"
                        mt={0.5}
                        textAlign="start"
                      >
                        ~
                        {formatNumber(
                          formatUnits(
                            calculatePrice(earning, 1, 18),
                            token.decimals,
                          ),
                          2,
                          6,
                        )}{" "}
                        USD
                      </Text>
                    )}
                  </>
                )
              )
            }
            return null
          })}
      </Box>
    )
  }

  return (
    <Box>
      {poolName === LCRO_WCRO_POOL_NAME && <CroPoolWarning mt={0} mb={2} />}
      <Box display="flex" justifyContent="space-between" alignItems="flex-end">
        <Box mr={2} width="70%">
          <AmountInput
            label={t("Stake")}
            value={stakeValue}
            onChange={setStakeValue}
            balance={lpWalletBalance}
            showMax
            hidePercentageButtons
            balanceLabel={t("Wallet Balance")}
            error={stakeError}
          />
        </Box>
        <Box width="30%" mb={stakeError !== null ? "35px" : 0}>
          <Button
            disabled={
              lpWalletBalance.isZero() ||
              parseStringToBigNumber(stakeValue, 18).value.isZero() ||
              stakeError !== null ||
              !account
            }
            onClick={() => setShowConfirmModal("stake")}
            style={{ height: 54 }}
            fullWidth
          >
            {t("Stake")}
          </Button>
        </Box>
      </Box>
      <Box
        mt={3}
        display="flex"
        justifyContent="space-between"
        alignItems="flex-end"
      >
        <Box mr={2} width="70%">
          <AmountInput
            label={t("Unstake")}
            value={unstakeValue}
            onChange={setUnstakeValue}
            balance={amountStaked}
            showMax
            hidePercentageButtons
            balanceLabel={t("Staked Balance")}
            error={unstakeError}
          />
        </Box>
        <Box width="30%" mb={unstakeError !== null ? "35px" : 0}>
          <Button
            fullWidth
            disabled={
              amountStaked.isZero() ||
              parseStringToBigNumber(unstakeValue, 18).value.isZero() ||
              unstakeError !== null ||
              !account
            }
            onClick={() => {
              if (
                parseStringToBigNumber(unstakeValue, 18).value.eq(
                  amountStaked,
                ) &&
                isBoosted
              ) {
                openLpModal()
              } else {
                setShowConfirmModal("unstake")
              }
            }}
            style={{ height: 54 }}
          >
            {t("Unstake")}
          </Button>
        </Box>
      </Box>
      <CustomCard variant="black50" mt={3}>
        <Text size="small">{t("Pending Rewards")}</Text>
        <Box
          display={isMobile ? "block" : "flex"}
          justifyContent="space-between"
          alignItems="flex-end"
        >
          {pool.version === 2 ? renderEarningsV2() : renderEarningsV1()}
          {account ? (
            <Button
              onClick={
                pool.version === 2
                  ? harvest
                  : () => {
                      setIsBoost(false)
                      openModal()
                    }
              }
              disabled={!enableHarvest}
              size="small"
              width={isMobile ? "100%" : "auto"}
              mt={isMobile ? 2 : 0}
            >
              {t("Harvest")}
            </Button>
          ) : (
            <Web3Status fullWidth />
          )}
        </Box>
        {boostedRewards.gt(0) && (
          <Box mt={1}>
            <Text size="small">{t("Boosted Rewards")}</Text>
            <Box
              display={isMobile ? "block" : "flex"}
              justifyContent="space-between"
              alignItems="flex-end"
            >
              <Box>
                <Text bold my={0.5}>{`${FER.symbol} ${formatNumber(
                  formatUnits(boostedRewards, FER.decimals),
                  2,
                  6,
                )}`}</Text>
                <FerroPriceText amount={boostedRewards} />
              </Box>
              <Button
                onClick={() => {
                  setIsBoost(true)
                  openModal()
                }}
                size="small"
                width={isMobile ? "100%" : "auto"}
                mt={isMobile ? 2 : 0}
              >
                {t("Harvest")}
              </Button>
            </Box>
          </Box>
        )}
        {(pool.version !== 2 || rewardBalance.gt("0")) && (
          <InfoSection
            marginTop="16px"
            content={
              <>
                When you harvest pool rewards, 40% of harvested amount will be
                converted to xFER and vested in one month of lock period. You
                can either choose to harvest remaining 60% directly to your
                wallet or stake into xFER vaults to enjoy higher APR.{" "}
                <Link
                  color="#fff"
                  fontWeight="bold"
                  target="_blank"
                  href="https://docs.ferroprotocol.com/user-guides/ferro-stake/lock-usdxfer-into-vaults"
                >
                  Learn more
                </Link>
              </>
            }
            img={PieIcon}
          />
        )}
      </CustomCard>
      <Modal
        onClose={() => setShowConfirmModal(false)}
        title={t("Confirm Transaction")}
        open={showConfirmModal !== false}
      >
        <Text mb={3}>
          {t("Please confirm the transaction in your wallet.")}
        </Text>
        <TransactionSummary data={summary} />
        <Button onClick={onConfirm} mt={3} fullWidth>
          {t("Confirm")}
        </Button>
      </Modal>
      <HarvestOptionModal
        open={showModal}
        onClose={closeModal}
        harvest={isBoost ? harvestBoost : harvest}
        harvestAmount={isBoost ? boostedRewards : rewardBalance}
        harvestMode={HarvestMode.Pool}
      />
      <UnstakeLpModal
        open={showLpModal}
        onClose={closeLpModal}
        onConfirm={() => {
          navigate("/pools")
          closeLpModal()
        }}
      />
      {openedDepositModal && (
        <DepositModal
          defaultSelectedPool={openedDepositModal.defaultSelectedPool}
        />
      )}
    </Box>
  )
}
